import React, { useState, useEffect, useCallback, forwardRef, Fragment } from 'react';
import { 
  CognitoIdentityProviderClient, 
  ListUsersCommand, 
  AdminAddUserToGroupCommand, 
  AdminRemoveUserFromGroupCommand,
  AdminDeleteUserCommand,
  AdminCreateUserCommand,
  AdminListGroupsForUserCommand,
  AdminUserGlobalSignOutCommand,
  UserType
} from '@aws-sdk/client-cognito-identity-provider';
import { 
  DynamoDBClient, 
  PutItemCommand, 
  DeleteItemCommand, 
  UpdateItemCommand 
} from '@aws-sdk/client-dynamodb';
import { marshall } from '@aws-sdk/util-dynamodb';
import { User } from '../../models/User';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XIcon, 
  TrashIcon, 
  ShieldCheckIcon,
  BriefcaseIcon,
  UserAddIcon,
  SearchIcon,
  CheckIcon,
  SelectorIcon,
  ExclamationIcon
} from '@heroicons/react/solid';
import { Listbox, Transition, Switch } from '@headlessui/react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { states } from '../Header/States';

interface AdminPortalProps {
  isOpen: boolean;
  onClose: () => void;
  dynamoDbClient: DynamoDBClient;
  allUsers: User[]; // DynamoDB users
  currentUser: User;
  onRefreshUsers: () => void;
  onShowAlert: (status: 'SUCCESS' | 'ERROR', message: string, subMessages?: string[]) => void;
}

const isDemo = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('demo') === 'true' || (window as any).isDemoMode) {
    sessionStorage.setItem('isDemoMode', 'true');
    return true;
  }
  return sessionStorage.getItem('isDemoMode') === 'true';
};

const CustomBirthdayInput = forwardRef(({ value, onClick, className }: any, ref: any) => (
  <button
    type="button"
    className={`${className} text-left flex items-center justify-between group`}
    onClick={onClick}
    ref={ref}
  >
    <span className={!value ? 'text-slate-400 dark:text-slate-500' : ''}>
      {value || 'Select Date'}
    </span>
    <SelectorIcon className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
  </button>
));

const COGNITO_GROUPS = ['Admins', 'Advisors', 'Post_College', 'Pre_College'] as const;
type CognitoGroup = typeof COGNITO_GROUPS[number];

const getGroupDisplayName = (group: string) => {
  if (group === 'Post_College') return 'Full Access';
  if (group === 'Pre_College') return 'Standard Access';
  return group.replace('_', ' ');
};

const USER_POOL_ID = 'us-east-1_8izkf6tti';

export const AdminPortal = ({ 
  isOpen, 
  onClose, 
  dynamoDbClient, 
  allUsers, 
  currentUser,
  onRefreshUsers,
  onShowAlert 
}: AdminPortalProps) => {
  const [cognitoUsers, setCognitoUsers] = useState<(UserType & { groups: string[] })[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showDeleteSpeedbump, setShowDeleteSpeedbump] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{username: string, cognitoUserId?: string, name: string} | null>(null);
  const [newUser, setNewUser] = useState<Partial<User>>({
    firstName: '',
    lastName: '',
    email: '',
    cellNumber: '',
    birthday: '',
    streetAddress: '',
    city: '',
    stateText: 'Connecticut',
    zipCode: '',
    darkMode: false,
    postCollege: false,
    advisor: false,
  });

  const resetNewUser = () => {
    setNewUser({
      firstName: '',
      lastName: '',
      email: '',
      cellNumber: '',
      birthday: '',
      streetAddress: '',
      city: '',
      stateText: 'Connecticut',
      zipCode: '',
      darkMode: false,
      postCollege: false,
      advisor: false,
    });
  };

  const getCognitoClient = useCallback(async () => {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const { credentials } = await fetchAuthSession();
    return new CognitoIdentityProviderClient({
      region: 'us-east-1',
      credentials
    });
  }, []);

  const fetchCognitoUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isDemo()) {
        // Mock Cognito users using DynamoDB users
        const mockCognitoUsers = allUsers.map(u => ({
          Username: u.cognitoUserId,
          Attributes: [
            { Name: 'email', Value: u.email },
            { Name: 'given_name', Value: u.firstName },
            { Name: 'family_name', Value: u.lastName },
          ],
          groups: u.advisor ? ['Admins', 'Advisors'] : (u.postCollege ? ['Post_College'] : ['Pre_College'])
        })) as any;
        setCognitoUsers(mockCognitoUsers);
        return;
      }

      const client = await getCognitoClient();
      const listCommand = new ListUsersCommand({ UserPoolId: USER_POOL_ID });
      const response = await client.send(listCommand);
      
      if (response.Users) {
        const usersWithGroups = await Promise.all(
          response.Users.map(async (user) => {
            const groupsCommand = new AdminListGroupsForUserCommand({
              UserPoolId: USER_POOL_ID,
              Username: user.Username || ''
            });
            const groupsResponse = await client.send(groupsCommand);
            return {
              ...user,
              groups: groupsResponse.Groups?.map(g => g.GroupName!) || []
            };
          })
        );
        setCognitoUsers(usersWithGroups);
      }
    } catch (error) {
      console.error('Error fetching Cognito users:', error);
      onShowAlert('ERROR', 'Failed to fetch Cognito users');
    } finally {
      setIsLoading(false);
    }
  }, [getCognitoClient, onShowAlert, allUsers]);

  useEffect(() => {
    if (isOpen) {
      fetchCognitoUsers();
      document.body.style.overflow = 'hidden';
      document.body.classList.add('immersive-active');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('immersive-active');
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('immersive-active');
    };
  }, [isOpen, fetchCognitoUsers]);

  const toggleGroup = async (username: string, group: CognitoGroup, isMember: boolean) => {
    try {
      if (isDemo()) {
        onShowAlert('SUCCESS', `Demo: User ${isMember ? 'removed from' : 'added to'} ${getGroupDisplayName(group)}`);
        return;
      }

      const client = await getCognitoClient();

      // Enforce mutual exclusivity for education/access groups
      if (!isMember) {
        if (group === 'Post_College') {
          await client.send(new AdminRemoveUserFromGroupCommand({
            UserPoolId: USER_POOL_ID,
            Username: username,
            GroupName: 'Pre_College'
          })).catch(() => console.info('Cleanup: User not in Pre_College'));
        } else if (group === 'Pre_College') {
          await client.send(new AdminRemoveUserFromGroupCommand({
            UserPoolId: USER_POOL_ID,
            Username: username,
            GroupName: 'Post_College'
          })).catch(() => console.info('Cleanup: User not in Post_College'));
        }
      }

      if (isMember) {
        await client.send(new AdminRemoveUserFromGroupCommand({
          UserPoolId: USER_POOL_ID,
          Username: username || '',
          GroupName: group
        }));
      } else {
        await client.send(new AdminAddUserToGroupCommand({
          UserPoolId: USER_POOL_ID,
          Username: username || '',
          GroupName: group
        }));
      }

      // Sync with DynamoDB if applicable
      const ddbUser = allUsers.find(u => u.cognitoUserId === username || u.email === username);
      if (ddbUser) {
        let updateExpression = '';
        const expressionAttributeValues: any = {};

        if (group === 'Advisors') {
          updateExpression = 'SET advisor = :val';
          expressionAttributeValues[':val'] = { BOOL: !isMember };
        } else if (group === 'Post_College') {
          updateExpression = 'SET postCollege = :val';
          expressionAttributeValues[':val'] = { BOOL: !isMember };
        } else if (group === 'Pre_College' && !isMember) {
          // Setting Standard Access (Pre_College) means postCollege is false
          updateExpression = 'SET postCollege = :val';
          expressionAttributeValues[':val'] = { BOOL: false };
        }

        if (updateExpression) {
          await dynamoDbClient.send(new UpdateItemCommand({
            TableName: 'MitchellFC',
            Key: { cognitoUserId: { S: ddbUser.cognitoUserId } },
            UpdateExpression: updateExpression,
            ExpressionAttributeValues: expressionAttributeValues
          }));
        }
      }

      // Force global sign out so they have to re-auth to get new group claims in their tokens
      await client.send(new AdminUserGlobalSignOutCommand({
        UserPoolId: USER_POOL_ID,
        Username: username
      }));

      fetchCognitoUsers();
      onRefreshUsers();
      onShowAlert('SUCCESS', `User ${isMember ? 'removed from' : 'added to'} ${getGroupDisplayName(group)}`, ['User sessions have been invalidated and they will need to sign in again.']);
    } catch (error) {
      console.error('Error toggling group:', error);
      onShowAlert('ERROR', 'Failed to update user groups');
    }
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    const { username, cognitoUserId } = userToDelete;

    try {
      if (isDemo()) {
        setShowDeleteSpeedbump(false);
        setUserToDelete(null);
        onShowAlert('SUCCESS', 'Demo: User deleted successfully');
        return;
      }

      const client = await getCognitoClient();
      await client.send(new AdminDeleteUserCommand({
        UserPoolId: USER_POOL_ID,
        Username: username
      }));

      const ddbUser = allUsers.find(u => u.cognitoUserId === (cognitoUserId || username) || u.email === username);
      if (ddbUser) {
        await dynamoDbClient.send(new DeleteItemCommand({
          TableName: 'MitchellFC',
          Key: { cognitoUserId: { S: ddbUser.cognitoUserId } }
        }));
      }

      setShowDeleteSpeedbump(false);
      setUserToDelete(null);
      fetchCognitoUsers();
      onRefreshUsers();
      setTimeout(() => {
        onShowAlert('SUCCESS', 'User deleted successfully');
      }, 400);
    } catch (error) {
      console.error('Error deleting user:', error);
      onShowAlert('ERROR', 'Failed to delete user');
    }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.firstName || !newUser.lastName) {
      onShowAlert('ERROR', 'Email, First Name, and Last Name are required');
      return;
    }

    try {
      if (isDemo()) {
        setIsCreateModalOpen(false);
        resetNewUser();
        onShowAlert('SUCCESS', 'Demo: User created successfully');
        return;
      }

      const client = await getCognitoClient();
      const createResponse = await client.send(new AdminCreateUserCommand({
        UserPoolId: USER_POOL_ID,
        Username: newUser.email,
        UserAttributes: [
          { Name: 'email', Value: newUser.email },
          { Name: 'email_verified', Value: 'true' },
          { Name: 'given_name', Value: newUser.firstName },
          { Name: 'family_name', Value: newUser.lastName },
        ],
        DesiredDeliveryMediums: ['EMAIL']
      }));

      const cognitoId = createResponse.User?.Username;
      if (!cognitoId) {
        throw new Error('Failed to retrieve Username from Cognito response');
      }

      const ddbItem: User = {
        cognitoUserId: cognitoId,
        firstName: newUser.firstName || '',
        lastName: newUser.lastName || '',
        email: newUser.email || '',
        cellNumber: newUser.cellNumber || 'N/A',
        birthday: newUser.birthday || '1900-01-01',
        streetAddress: newUser.streetAddress || 'N/A',
        city: newUser.city || 'N/A',
        stateText: newUser.stateText || 'Connecticut',
        zipCode: newUser.zipCode || '00000',
        darkMode: false,
        postCollege: newUser.postCollege || false,
        advisor: newUser.advisor || false,
        birthdayEmails: true,
        documentEmails: true,
        postEmails: true
      };

      await dynamoDbClient.send(new PutItemCommand({
        TableName: 'MitchellFC',
        Item: marshall(ddbItem)
      }));

      // Add to groups
      if (newUser.advisor) {
        await client.send(new AdminAddUserToGroupCommand({
          UserPoolId: USER_POOL_ID,
          Username: cognitoId,
          GroupName: 'Advisors'
        }));
      } else {
        const educationGroup = newUser.postCollege ? 'Post_College' : 'Pre_College';
        await client.send(new AdminAddUserToGroupCommand({
          UserPoolId: USER_POOL_ID,
          Username: cognitoId,
          GroupName: educationGroup
        }));
      }

      setIsCreateModalOpen(false);
      resetNewUser();
      fetchCognitoUsers();
      onRefreshUsers();
      setTimeout(() => {
        onShowAlert('SUCCESS', 'User created successfully');
      }, 400);
    } catch (error: any) {
      console.error('Error creating user:', error);
      onShowAlert('ERROR', 'Failed to create user', [error.message]);
    }
  };

  const filteredUsers = cognitoUsers.filter(u => {
    const email = u.Attributes?.find(a => a.Name === 'email')?.Value?.toLowerCase() || '';
    const givenName = u.Attributes?.find(a => a.Name === 'given_name')?.Value?.toLowerCase() || '';
    const familyName = u.Attributes?.find(a => a.Name === 'family_name')?.Value?.toLowerCase() || '';
    const search = searchQuery.toLowerCase().trim();
    
    if (!search) return true;

    // Try to find the user in our DynamoDB list for more reliable name searching
    const ddbUser = allUsers.find(du => du.cognitoUserId === u.Username || du.email?.toLowerCase() === email);
    const ddbFirstName = ddbUser?.firstName?.toLowerCase() || '';
    const ddbLastName = ddbUser?.lastName?.toLowerCase() || '';
    
    const fullName = `${givenName} ${familyName}`.trim();
    const ddbFullName = `${ddbFirstName} ${ddbLastName}`.trim();

    return email.includes(search) || 
           givenName.includes(search) || 
           familyName.includes(search) || 
           fullName.includes(search) ||
           ddbFirstName.includes(search) ||
           ddbLastName.includes(search) ||
           ddbFullName.includes(search);
  }).sort((a, b) => {
    const aEmail = a.Attributes?.find(attr => attr.Name === 'email')?.Value || '';
    const bEmail = b.Attributes?.find(attr => attr.Name === 'email')?.Value || '';
    
    const aDdb = allUsers.find(du => du.cognitoUserId === a.Username || du.email?.toLowerCase() === aEmail.toLowerCase());
    const bDdb = allUsers.find(du => du.cognitoUserId === b.Username || du.email?.toLowerCase() === bEmail.toLowerCase());

    const aFirst = (aDdb?.firstName || a.Attributes?.find(attr => attr.Name === 'given_name')?.Value || 'Unknown').toLowerCase();
    const bFirst = (bDdb?.firstName || b.Attributes?.find(attr => attr.Name === 'given_name')?.Value || 'Unknown').toLowerCase();
    
    if (aFirst < bFirst) return -1;
    if (aFirst > bFirst) return 1;

    const aLast = (aDdb?.lastName || a.Attributes?.find(attr => attr.Name === 'family_name')?.Value || '').toLowerCase();
    const bLast = (bDdb?.lastName || b.Attributes?.find(attr => attr.Name === 'family_name')?.Value || '').toLowerCase();

    if (aLast < bLast) return -1;
    if (aLast > bLast) return 1;
    
    return 0;
  });

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/80 sm:bg-slate-900/70 z-[150] flex items-start justify-center p-2 sm:p-4 overflow-y-auto transform-gpu"
        onClick={onClose}
      >
        <motion.div
          id="admin-portal-container"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="bg-white dark:bg-slate-800 w-full max-w-5xl h-full max-h-[85vh] sm:max-h-[90vh] rounded-2xl sm:rounded-3xl shadow-2xl flex flex-col mt-4 sm:mt-12 overflow-hidden transform-gpu"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start justify-between sm:justify-start sm:gap-4 flex-1">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                  <ShieldCheckIcon className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">Admin User Portal</h2>
                  <p className="text-[10px] sm:text-sm text-slate-500 dark:text-slate-400 font-medium">Manage family permissions</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="sm:hidden p-2 -mt-1 -mr-1 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 transition-colors"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-[background-color,color,box-shadow,transform] shadow-lg shadow-indigo-500/20 active:scale-95 whitespace-nowrap"
              >
                <UserAddIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                Add User
              </button>
              <button
                onClick={onClose}
                className="hidden sm:flex p-2.5 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 transition-colors"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="p-3 sm:p-4 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700">
            <div className="relative group">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 sm:py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl sm:rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 transition-[background-color,border-color,ring] text-base sm:text-base font-medium text-slate-900 dark:text-white dark:placeholder-slate-500"
              />
            </div>
          </div>

          {/* User List */}
          <div className="flex-grow overflow-y-auto p-3 sm:p-4 custom-scrollbar">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-slate-500 dark:text-slate-400 font-bold animate-pulse uppercase tracking-widest text-[10px]">Loading Cognito Records...</p>
              </div>
            ) : (
              <div className="grid gap-3 sm:gap-4">
                {filteredUsers.map((u) => {
                  const email = u.Attributes?.find(a => a.Name === 'email')?.Value || 'No Email';
                  const ddbUser = allUsers.find(du => du.cognitoUserId === u.Username || du.email?.toLowerCase() === email.toLowerCase());
                  
                  // Prioritize names from DynamoDB as they are the source of truth for our app's profile
                  const firstName = ddbUser?.firstName || u.Attributes?.find(a => a.Name === 'given_name')?.Value || 'Unknown';
                  const lastName = ddbUser?.lastName || u.Attributes?.find(a => a.Name === 'family_name')?.Value || '';
                  const isAdvisor = u.groups.includes('Advisors');
                  
                  return (
                    <div key={u.Username} className="bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4 group hover:border-indigo-500/30 transition-all">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg sm:rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center font-black text-xs sm:text-base text-slate-400 border border-slate-200 dark:border-slate-700 shrink-0">
                          {firstName.charAt(0)}{lastName ? lastName.charAt(0) : ''}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-black text-base sm:text-lg text-slate-900 dark:text-white truncate">{firstName} {lastName}</h4>
                          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium truncate">{email}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {u.groups.map(g => (
                              <span key={g} className="px-2 py-0.5 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[9px] sm:text-[10px] font-black uppercase tracking-wider border border-indigo-200/50 dark:border-indigo-500/20">
                                {getGroupDisplayName(g)}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 pt-3 lg:pt-0 border-t lg:border-t-0 border-slate-200/50 dark:border-slate-700/50">
                        {['Admins', 'Advisors'].map(group => {
                          const isMember = u.groups.includes(group);
                          return (
                            <button
                              key={group}
                              onClick={() => toggleGroup(u.Username!, group as CognitoGroup, isMember)}
                              className={`px-2.5 py-1.5 sm:px-3 rounded-lg sm:rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all border ${
                                isMember 
                                  ? 'bg-indigo-600 border-indigo-600 text-white shadow-md' 
                                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-indigo-500'
                              }`}
                            >
                              {getGroupDisplayName(group)}
                            </button>
                          );
                        })}
                        
                        <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1"></div>

                        <div className={`flex bg-slate-100 dark:bg-slate-950 p-1 rounded-xl ${isAdvisor ? 'opacity-50 grayscale' : ''}`} title={isAdvisor ? 'Advisors have fixed full access' : ''}>
                          <button
                            disabled={isAdvisor}
                            onClick={() => !u.groups.includes('Pre_College') && toggleGroup(u.Username!, 'Pre_College', false)}
                            className={`px-3 py-1.5 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all ${
                              u.groups.includes('Pre_College') && !isAdvisor
                                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm border dark:border-slate-600' 
                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                          >
                            Standard
                          </button>
                          <button
                            disabled={isAdvisor}
                            onClick={() => !u.groups.includes('Post_College') && toggleGroup(u.Username!, 'Post_College', false)}
                            className={`px-3 py-1.5 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all ${
                              u.groups.includes('Post_College') || isAdvisor
                                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm border dark:border-slate-600' 
                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                          >
                            Full
                          </button>
                        </div>

                        <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1"></div>
                        <button
                          onClick={() => {
                            setUserToDelete({
                              username: u.Username!,
                              cognitoUserId: ddbUser?.cognitoUserId,
                              name: `${firstName} ${lastName}`
                            });
                            setShowDeleteSpeedbump(true);
                          }}
                          disabled={u.Username === currentUser.cognitoUserId}
                          className="ml-auto lg:ml-0 p-2 rounded-lg sm:rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30 disabled:pointer-events-none transition-all"
                          title="Delete User"
                        >
                          <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>

        {/* Delete User Speedbump */}
        {createPortal(
          <AnimatePresence>
            {showDeleteSpeedbump && (
              <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                <motion.div 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  exit={{ opacity: 0 }} 
                  className="absolute inset-0 bg-slate-950/95 transform-gpu" 
                  onClick={() => setShowDeleteSpeedbump(false)} 
                />
                <motion.div 
                  initial={{ scale: 0.9, opacity: 0 }} 
                  animate={{ scale: 1, opacity: 1 }} 
                  exit={{ scale: 0.9, opacity: 0 }} 
                  className="relative bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl max-w-md w-full border dark:border-white/10 transform-gpu" 
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center">
                    <div className="h-14 w-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4 text-red-600 dark:text-red-400">
                      <ExclamationIcon className="h-7 w-7" />
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Delete User?</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 leading-relaxed text-center">
                      Are you sure you want to delete <span className="font-bold text-slate-900 dark:text-white">{userToDelete?.name}</span>? This will permanently remove them from both Cognito and the database. This action cannot be undone.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button 
                        type="button" 
                        onClick={deleteUser} 
                        className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-red-700 transition-[background-color,transform,shadow] shadow-lg active:scale-95 order-1 sm:order-2"
                      >
                        Delete User
                      </button>
                      <button 
                        type="button" 
                        onClick={() => {
                          setShowDeleteSpeedbump(false);
                          setUserToDelete(null);
                        }} 
                        className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-slate-200 transition-colors active:scale-95 order-2 sm:order-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Create User Modal */}
        <AnimatePresence>
          {isCreateModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/90 z-[200] flex items-start justify-center p-2 sm:p-4 sm:items-center overflow-y-auto transform-gpu"
              onClick={() => setIsCreateModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-800 w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] rounded-2xl sm:rounded-[2.5rem] shadow-2xl flex flex-col my-auto overflow-hidden transform-gpu"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-5 sm:p-8 overflow-y-auto custom-scrollbar">
                  <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                    <div className="h-10 w-10 sm:h-14 sm:w-14 rounded-xl sm:rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                      <UserAddIcon className="h-6 w-6 sm:h-8 sm:w-8" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">Create New User</h3>
                      <p className="text-[10px] sm:text-sm text-slate-500 dark:text-slate-400 font-medium">Invited users will receive an email to set their password</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="sm:col-span-2">
                      <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Email Address</label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="w-full px-4 py-2.5 sm:py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-base sm:text-base text-slate-900 dark:text-white font-medium dark:placeholder-slate-500"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">First Name</label>
                      <input
                        type="text"
                        value={newUser.firstName}
                        onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                        className="w-full px-4 py-2.5 sm:py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-base sm:text-base text-slate-900 dark:text-white font-medium dark:placeholder-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Last Name</label>
                      <input
                        type="text"
                        value={newUser.lastName}
                        onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                        className="w-full px-4 py-2.5 sm:py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 text-base sm:text-base text-slate-900 dark:text-white font-medium dark:placeholder-slate-500"
                      />
                    </div>

                    <div className="sm:col-span-2 flex flex-col gap-4 border-t border-slate-100 dark:border-slate-700 pt-6 mt-2">
                      <label className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 cursor-pointer group transition-colors hover:bg-slate-100 dark:hover:bg-slate-800/50">
                        <div className="flex items-center gap-3">
                          <BriefcaseIcon className="h-5 w-5 text-slate-400 group-hover:text-indigo-500 transition-colors" />
                          <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Advisor Role</span>
                        </div>
                        <Switch
                          checked={newUser.advisor || false}
                          onChange={(checked: boolean) => setNewUser({...newUser, advisor: checked})}
                          className={`${
                            newUser.advisor ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700'
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900`}
                        >
                          <span
                            className={`${
                              newUser.advisor ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </label>
                    </div>

                    {!newUser.advisor && (
                      <Fragment>
                        <div>
                          <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Cell number</label>
                          <input
                            type="tel"
                            className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-base sm:text-base text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium dark:placeholder-slate-500"
                            value={newUser.cellNumber}
                            onChange={(e) => setNewUser({ ...newUser, cellNumber: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Birthday</label>
                          <DatePicker
                            showMonthDropdown
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={100}
                            maxDate={new Date()}
                            withPortal={isMobile}
                            customInput={<CustomBirthdayInput className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-sm sm:text-base text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium" />}
                            selected={newUser.birthday ? new Date(newUser.birthday + 'T00:00:00') : null}
                            onChange={(date: Date | null) => {
                              if (date) {
                                const y = date.getFullYear();
                                const m = String(date.getMonth() + 1).padStart(2, '0');
                                const d = String(date.getDate()).padStart(2, '0');
                                setNewUser({ ...newUser, birthday: `${y}-${m}-${d}` });
                              }
                            }}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Street address</label>
                          <input
                            type="text"
                            className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-base sm:text-base text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium dark:placeholder-slate-500"
                            value={newUser.streetAddress}
                            onChange={(e) => setNewUser({ ...newUser, streetAddress: e.target.value })}
                          />
                        </div>
                        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">City</label>
                            <input
                              type="text"
                              className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-base sm:text-base text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium dark:placeholder-slate-500"
                              value={newUser.city}
                              onChange={(e) => setNewUser({ ...newUser, city: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">State</label>
                            <Listbox
                              value={newUser.stateText}
                              onChange={(value) => setNewUser({ ...newUser, stateText: value })}
                            >
                              <div className="relative">
                                <Listbox.Button className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-sm sm:text-base text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-left font-medium">
                                  <span className="block truncate">{newUser.stateText}</span>
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <SelectorIcon className="w-5 h-5 text-slate-400" aria-hidden="true" />
                                  </span>
                                </Listbox.Button>
                                <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                                  <Listbox.Options className="absolute w-full py-1 mt-2 overflow-auto text-base bg-white dark:bg-slate-800 rounded-xl shadow-xl max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-[210] custom-scrollbar">
                                    {states.map((state, i) => (
                                      <Listbox.Option
                                        key={i}
                                        className={({ active }) => `cursor-pointer select-none relative py-2.5 pl-10 pr-4 transition-colors ${active ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-200'}`}
                                        value={state}
                                      >
                                        {({ selected }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-bold text-indigo-600 dark:text-indigo-400' : 'font-medium'}`}>{state}</span>
                                            {selected && <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 dark:text-indigo-400"><CheckIcon className="w-5 h-5" aria-hidden="true" /></span>}
                                          </>
                                        )}
                                      </Listbox.Option>
                                    ))}
                                  </Listbox.Options>
                                </Transition>
                              </div>
                            </Listbox>
                          </div>
                          <div>
                            <label className="block mb-2 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Zip code</label>
                            <input
                              type="text"
                              className="w-full px-4 py-2.5 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-base sm:text-base text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium dark:placeholder-slate-500"
                              value={newUser.zipCode}
                              onChange={(e) => setNewUser({ ...newUser, zipCode: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-700">
                          <label className="block mb-3 text-xs font-black text-slate-400 uppercase tracking-widest">Document Access Level</label>
                          <div className="grid grid-cols-2 gap-2 bg-slate-100 dark:bg-slate-950 p-1 rounded-xl">
                            <button
                              type="button"
                              onClick={() => setNewUser({ ...newUser, postCollege: false })}
                              className={`py-2.5 rounded-lg text-sm font-black uppercase tracking-wider transition-all ${!newUser.postCollege ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-md border dark:border-slate-600' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
                            >
                              Standard
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewUser({ ...newUser, postCollege: true })}
                              className={`py-2.5 rounded-lg text-sm font-black uppercase tracking-wider transition-all ${newUser.postCollege ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-md border dark:border-slate-600' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
                            >
                              Full
                            </button>
                          </div>
                        </div>
                      </Fragment>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 sm:mt-10">
                    <button
                      onClick={() => setIsCreateModalOpen(false)}
                      className="order-2 sm:order-1 flex-1 px-6 py-3 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 font-bold rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-900 transition-all text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createUser}
                      className="order-1 sm:order-2 flex-1 px-6 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-500/20 text-sm sm:text-base"
                    >
                      Create Account
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};
